import { useCallback, useState } from "react";

import type { OrgChannelSummary } from "../blackboard/useOrgChannels";

import { SIGNAL_HEX } from "../common/colorTokens";

/**
 * Shared room-creation primitive — the single owner of "make a new shared
 * channel" for the whole dashboard. Rooms (NewRoomDialog), Spawn
 * (RoomPickerField) and any future surface call THIS, instead of forking the
 * actor-scoped create-and-join in ChannelPickerDialog (which requires an
 * instance to already exist). Wraps the standalone `POST /user/channels`
 * endpoint; no backend change needed.
 *
 * A room IS a shared channel (private channels are not rooms — see the IA
 * reshape doc). The created channel is returned mapped to OrgChannelSummary so
 * callers can navigate to /rooms/:id immediately without waiting for the
 * realtime-driven channel list to refresh.
 */

const getResponseErrorMessage = (data: unknown, fallback: string): string =>
  typeof data === "object" &&
  data !== null &&
  "message" in data &&
  typeof data.message === "string"
    ? data.message
    : fallback;

/** Channel names are slugs: lowercased, whitespace collapsed to hyphens. */
export function normalizeRoomName(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, "-");
}

interface UseRoomCreation {
  createRoom: (displayName: string) => Promise<OrgChannelSummary>;
  creating: boolean;
  error: string | null;
  clearError: () => void;
}

export function useRoomCreation(): UseRoomCreation {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const createRoom = useCallback(async (displayNameRaw: string) => {
    const displayName = normalizeRoomName(displayNameRaw);
    if (!displayName) {
      const message = "Enter a room name.";
      setError(message);
      throw new Error(message);
    }
    setCreating(true);
    setError(null);
    try {
      const res = await fetch("/user/channels", {
        body: JSON.stringify({ displayName }),
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });
      if (!res.ok) {
        const data: unknown = await res.json().catch(() => ({}));
        throw new Error(getResponseErrorMessage(data, `HTTP ${res.status}`));
      }
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const raw = (await res.json()) as {
        id: string;
        displayName: string;
        createdAt: string;
        organizationId: string;
        provenance: "issue_workspace" | "manual" | "teams_bind";
      };
      const channel: OrgChannelSummary = {
        accentColor: SIGNAL_HEX.accent,
        activeTeamsBindingId: null,
        activeTeamsConversationId: null,
        activeTeamsConversationLabel: null,
        createdAt: raw.createdAt,
        displayName: raw.displayName,
        environmentStatus: null,
        hasDetachedTeamsBinding: false,
        id: raw.id,
        kind: "shared",
        lastActivityAt: null,
        memberAvatarSeeds: [],
        memberCount: 0,
        organizationId: raw.organizationId,
        ownerActorId: null,
        provenance: raw.provenance,
        teamsBindingChannelKind: null,
      };
      return channel;
    } catch (creationError) {
      const message =
        creationError instanceof Error
          ? creationError.message
          : "Failed to create channel";
      setError(message);
      throw creationError instanceof Error ? creationError : new Error(message);
    } finally {
      setCreating(false);
    }
  }, []);

  return { clearError, createRoom, creating, error };
}
